'use client'

import { useState, useMemo } from 'react'
import { Plus, X } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select-radix'

interface RoleGroupSelectProps {
  value: string | null | undefined
  onChange: (value: string | null) => void
  existingGroups: string[]
  disabled?: boolean
  placeholder?: string
}

/**
 * Role Group Select with inline creation capability
 * Allows selecting from existing groups or creating a new one on the fly
 */
export function RoleGroupSelect({
  value,
  onChange,
  existingGroups,
  disabled = false,
  placeholder = 'Selecione um grupo...'
}: RoleGroupSelectProps) {
  const [isCreating, setIsCreating] = useState(false)
  const [newGroupName, setNewGroupName] = useState('')
  const [localNewGroups, setLocalNewGroups] = useState<string[]>([])

  // Combine existing groups with locally created ones, ensuring no duplicates
  const allGroups = useMemo(() => {
    const combined = new Set([...existingGroups, ...localNewGroups])
    return Array.from(combined).sort((a, b) => a.localeCompare(b, 'pt-BR'))
  }, [existingGroups, localNewGroups])

  const handleCreate = () => {
    const trimmedName = newGroupName.trim()
    if (trimmedName) {
      // Add to local new groups so it appears in dropdown
      if (!allGroups.includes(trimmedName)) {
        setLocalNewGroups(prev => [...prev, trimmedName])
      }
      onChange(trimmedName)
      setNewGroupName('')
      setIsCreating(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleCreate()
    } else if (e.key === 'Escape') {
      setIsCreating(false)
      setNewGroupName('')
    }
  }

  if (isCreating) {
    return (
      <div className="flex gap-2">
        <Input
          value={newGroupName}
          onChange={(e) => setNewGroupName(e.target.value)}
          placeholder="Nome do novo grupo..."
          className="flex-1 bg-brand-dark-lighter border-brand-gray/20 text-white placeholder:text-brand-gray"
          onKeyDown={handleKeyDown}
          autoFocus
          disabled={disabled}
        />
        <Button
          type="button"
          size="sm"
          onClick={handleCreate}
          disabled={!newGroupName.trim() || disabled}
          className="bg-brand-primary hover:bg-brand-primary/90"
        >
          Criar
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={() => {
            setIsCreating(false)
            setNewGroupName('')
          }}
          disabled={disabled}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    )
  }

  // If there's a value that's not in allGroups, add it temporarily for display
  const displayGroups = value && !allGroups.includes(value)
    ? [...allGroups, value].sort((a, b) => a.localeCompare(b, 'pt-BR'))
    : allGroups

  return (
    <div className="flex gap-2">
      <Select
        value={value || '__none__'}
        onValueChange={(v) => onChange(v === '__none__' ? null : v)}
        disabled={disabled}
      >
        <SelectTrigger className="flex-1 bg-brand-dark-lighter border-brand-gray/20 text-white">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__none__">Sem grupo</SelectItem>
          {displayGroups.map((group) => (
            <SelectItem key={group} value={group}>
              {group}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={() => setIsCreating(true)}
        disabled={disabled}
        title="Criar novo grupo"
        className="border-brand-gray/20 hover:bg-brand-dark-lighter"
      >
        <Plus className="h-4 w-4" />
      </Button>
    </div>
  )
}

export default RoleGroupSelect
